import {
  deserializeRpcError,
  RpcError,
  type SerializedError,
} from '../core/error'
import type { SubscriptionHandle, SubscriptionHandlers } from '../core/types'
import type { BoundaryTelemetry } from '@w/tel'

export interface SubscriptionSink<TItem> {
  item: (value: TItem) => void
  end: (error?: Error) => void
}

type Waiter<TItem> = {
  resolve: (result: IteratorResult<TItem>) => void
  reject: (error: unknown) => void
}

/**
 * Bridges a pushed stream into both delivery surfaces: callbacks and
 * `for await`. `unsubscribe()` is idempotent and stops delivery immediately,
 * including for records already in flight.
 */
export function createSubscriptionHandle<TItem>(
  start: (
    sink: SubscriptionSink<TItem>,
    signal: AbortSignal,
  ) => Promise<void> | void,
  handlers?: SubscriptionHandlers<TItem>,
): SubscriptionHandle<TItem> {
  const abort = new AbortController()
  const buffer: TItem[] = []
  const waiters: Waiter<TItem>[] = []
  let iterating = false
  let ended = false
  let endError: Error | undefined
  let closed = false

  const sink: SubscriptionSink<TItem> = {
    item(value) {
      if (closed || ended) return
      handlers?.onData?.(value)

      const waiter = waiters.shift()
      if (waiter) {
        waiter.resolve({ done: false, value })
        return
      }
      if (iterating || !handlers?.onData) buffer.push(value)
    },

    end(error) {
      if (closed || ended) return
      ended = true
      endError = error

      if (error) handlers?.onError?.(error)
      else handlers?.onComplete?.()

      for (const waiter of waiters.splice(0)) {
        if (error) waiter.reject(error)
        else waiter.resolve({ done: true, value: undefined })
      }
    },
  }

  const unsubscribe = () => {
    if (closed) return
    closed = true
    buffer.length = 0
    abort.abort()
    for (const waiter of waiters.splice(0)) {
      waiter.resolve({ done: true, value: undefined })
    }
  }

  void (async () => {
    try {
      await start(sink, abort.signal)
    } catch (error) {
      if (abort.signal.aborted) return
      sink.end(error instanceof Error ? error : new Error(String(error)))
    }
  })()

  return {
    unsubscribe,

    [Symbol.asyncIterator]() {
      iterating = true
      return {
        next(): Promise<IteratorResult<TItem>> {
          if (buffer.length > 0) {
            return Promise.resolve({
              done: false,
              value: buffer.shift() as TItem,
            })
          }
          if (closed) return Promise.resolve({ done: true, value: undefined })
          if (ended) {
            return endError
              ? Promise.reject(endError)
              : Promise.resolve({ done: true, value: undefined })
          }
          return new Promise<IteratorResult<TItem>>((resolve, reject) => {
            waiters.push({ resolve, reject })
          })
        },
        return(): Promise<IteratorResult<TItem>> {
          unsubscribe()
          return Promise.resolve({ done: true, value: undefined })
        },
      }
    },
  }
}

/**
 * Reads NDJSON records into a sink. Ends the sink exactly once: on the terminal
 * record, on a truncated body, or on a transport failure.
 */
export async function readSubscriptionStream<TItem>(
  body: ReadableStream<Uint8Array>,
  sink: SubscriptionSink<TItem>,
  signal: AbortSignal,
  onTelemetry?: (telemetry: BoundaryTelemetry) => void,
): Promise<void> {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let pending = ''

  const consumeLine = (line: string): boolean => {
    const trimmed = line.trim()
    if (trimmed === '') return false

    const record = JSON.parse(trimmed) as {
      item?: TItem
      end?: { error?: SerializedError; telemetry?: BoundaryTelemetry }
    }

    if (record.end) {
      const { error, telemetry } = record.end
      if (telemetry) onTelemetry?.(telemetry)
      sink.end(error ? deserializeRpcError(error) : undefined)
      return true
    }

    sink.item(record.item as TItem)
    return false
  }

  try {
    while (!signal.aborted) {
      const { done, value } = await reader.read()
      if (done) break

      // Search only the new chunk for line breaks (split2/ndjson practice): rescanning
      // `pending` per chunk is quadratic in the size of one large item.
      const chunk = decoder.decode(value, { stream: true })
      let start = 0
      let newline = chunk.indexOf('\n')
      while (newline >= 0) {
        const line = pending + chunk.slice(start, newline)
        pending = ''
        if (consumeLine(line)) return
        start = newline + 1
        newline = chunk.indexOf('\n', start)
      }
      pending += chunk.slice(start)
    }

    if (!signal.aborted && consumeLine(pending)) return
  } catch (error) {
    if (signal.aborted) return
    sink.end(error instanceof Error ? error : new Error(String(error)))
    return
  } finally {
    reader.cancel().catch(() => {})
  }

  if (signal.aborted) return
  sink.end(new RpcError(500, 'Stream ended without a terminal record'))
}
